/* eslint-disable react-hooks/exhaustive-deps */
import BackButton from "components/BackButton";
import DirectorNotesButton from "components/DirectorNotesButton";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import SuspectDetailInfo from "pages/Incident/IncidentDetail/modals/SuspectDetailInfo";
import InstituteOfChargeDetail from "pages/InstituteOfCharge/InstituteOfChargeDetail";
import AppealRequestFromDefendantForm from "pages/InstituteOfCharge/modal/AppealRequestFromDefendantForm";
import DefendantAppealRequestForm from "pages/InstituteOfCharge/modal/DefendantAppealRequestForm";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  editDefendantAppeal2,
  fetchDefendantAppealById,
} from "services/appealService";
import {
  AppealDailyReport,
  AppealRequestStatus,
  DefendantAppeal,
  DefendantAppealStatus,
  FEEDBACK_MODEL,
  FILE_URL,
  Roles,
  Suspect,
} from "types";
import { DateView, errorHandlerOnForm, hasRole } from "utils/helper";
import DefendantAppealReport from "./DefendantAppealReport";
import DefendantAppealResponseForm from "./DefendantAppealResponseForm";
import AppealDailyReportFeedbackSection from "./fragment/AppealDailyReportFeedback";
import AppealFeedbackSection from "./fragment/AppealFeedback";
import AppealRespondbackFeedback from "./fragment/AppealRespondbackFeedback";
import DefendantAssignProsecutorPanel from "./fragment/DefendantAssignProsecutorPanel";
import PreviousAppealView from "./fragment/PreviousAppealView";
import DefendantForwardToCourtPanel from "./fragment/DefendantForwardToCourtPanel";
import RespondBackComment from "./modal/RespondBackComment";
import DocumentViewerModal from "components/DocumentViewer";

// Enhanced status chip with better styling
export function getStatusChipColor(status: string) {
  const baseClasses =
    "inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border";

  switch (status) {
    case AppealRequestStatus.PENDING:
      return `${baseClasses} bg-amber-50 text-amber-800 border-amber-200`;
    case AppealRequestStatus.FORWARDED_TO_REGISTRAR:
      return `${baseClasses} bg-blue-50 text-blue-800 border-blue-200`;
    case AppealRequestStatus.FORWARDED_TO_COORDINATOR:
      return `${baseClasses} bg-purple-50 text-purple-800 border-purple-200`;
    case AppealRequestStatus.SUBMITTED_TO_COURT:
      return `${baseClasses} bg-emerald-50 text-emerald-800 border-emerald-200`;
    case AppealRequestStatus.COORDINATOR_RESPONDED_BACK:
      return `${baseClasses} bg-pink-50 text-pink-800 border-pink-200`;
    case AppealRequestStatus.ASSIGNED_TO_PROSECUTOR:
      return `${baseClasses} bg-indigo-50 text-indigo-800 border-indigo-200`;
    case AppealRequestStatus.FORWARDED_TO_HIGHER_REGISTRAR:
      return `${baseClasses} bg-blue-100 text-blue-900 border-blue-300`;
    case AppealRequestStatus.FORWARDED_TO_HIGHER_CLERK:
      return `${baseClasses} bg-gray-100 text-gray-900 border-gray-300`;
    case AppealRequestStatus.FORWARDED_TO_HIGHER_COORDINATOR:
      return `${baseClasses} bg-purple-100 text-purple-900 border-purple-300`;
    case AppealRequestStatus.RESPONDED_BACK_TO_PROSECUTOR:
      return `${baseClasses} bg-orange-50 text-orange-800 border-orange-200`;
    case AppealRequestStatus.COMPLETED:
      return `${baseClasses} bg-green-100 text-green-900 border-green-300`;
    default:
      return `${baseClasses} bg-gray-100 text-gray-800 border-gray-300`;
  }
}

// Reusable Components
const InfoCard: React.FC<{
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  badgeCount?: number;
}> = ({ title, children, defaultOpen = false, badgeCount }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <button
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {badgeCount !== undefined && (
            <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full font-medium">
              {badgeCount}
            </span>
          )}
        </div>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {isOpen && <div className="px-6 pb-4">{children}</div>}
    </div>
  );
};

const InfoRow: React.FC<{
  label: string;
  value: React.ReactNode;
  className?: string;
}> = ({ label, value, className = "" }) => (
  <div
    className={`flex justify-between items-start py-3 border-b border-gray-100 last:border-b-0 ${className}`}
  >
    <dt className="text-sm font-medium text-gray-600 flex-shrink-0 pr-4">
      {label}
    </dt>
    <dd className="text-sm text-gray-900 text-right font-medium">{value}</dd>
  </div>
);

const FileAttachment: React.FC<{ name: string; url: string }> = ({
  name,
  url,
}) => (
  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
        <svg
          className="w-5 h-5 text-red-600"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
            clipRule="evenodd"
          />
        </svg>
      </div>
      <span className="font-medium text-gray-900">{name}</span>
    </div>
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="px-3 py-1 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors"
    >
      View
    </a>
  </div>
);

const ActionButton: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "outline";
  className?: string;
}> = ({ onClick, children, variant = "outline", className = "" }) => {
  const baseClasses =
    "px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-2";
  const variants = {
    primary: "bg-red-600 text-white hover:bg-red-700 shadow-sm",
    secondary: "bg-gray-600 text-white hover:bg-gray-700 shadow-sm",
    outline: "border border-gray-300 text-gray-700 hover:bg-gray-50",
  };

  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${className}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

const ActionDropdownItem: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  tone?: "primary" | "success" | "warning" | "neutral";
}> = ({ onClick, children, tone = "primary" }) => {
  const tones: Record<string, string> = {
    primary: "bg-red-600 text-white hover:bg-red-700 shadow-sm",
    success: "bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm",
    warning:
      "border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100",
    neutral: "border border-gray-300 text-gray-700 hover:bg-gray-50",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg transition-colors ${tones[tone]}`}
    >
      {children}
    </button>
  );
};

const DefendantAppealDetail: React.FC = () => {
  const { id } = useParams();
  const { loginData } = useAuth();
  const navigate = useNavigate();
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();

  const [loading, setLoading] = React.useState(false);
  // Dropdown removed (actions render inline in the tabbed panel); the setter is
  // still called by the action handlers as a harmless no-op.
  const [, setActionDropdownOpen] = useState(false);
  const [openRespondBack, setOpenRespondBack] = useState(false);
  const [openResponseForm, setOpenResponseForm] = useState(false);
  const [selectedDailyReport, setSelectedAppealDailyReport] =
    useState<AppealDailyReport>();
  const [openEditAppeal, setOpenEditAppeal] = useState(false);
  const [openAppealForm, setOpenAppealForm] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState<number>();
  const [selectedDefendant, setSelectedDefendant] = useState<Suspect>();
  const [appeal, setAppeal] = useState<DefendantAppeal>();
  // When the record is a cassation, labels read "Cassation" instead of "Appeal".
  const isCass = !!appeal?.is_casations;
  const [activeSidebar, setActiveSidebar] = useState<
    "normalFeedback" | "respondBack" | "dailyReport"
  >("normalFeedback");
  const [rightTab, setRightTab] = useState<"actions" | "feedback">("actions");
  const [activeMainPage, setActiveMainPage] = useState<
    "appeal" | "higherAdmin" | "report" | "previousAppeal"
  >("appeal");

  // Cassations originate from a prior appeal (prosecutor or defendant).
  const previousProsecutorAppealId =
    (appeal as any)?.originating_prosecutor_appeal ?? null;
  const previousDefendantAppealId = (appeal as any)?.parent_appeal ?? null;
  const hasPreviousAppeal = !!(
    previousProsecutorAppealId || previousDefendantAppealId
  );
  // Report and previous-appeal use the full page (actions/feedback hidden).
  const isFullPageView =
    activeMainPage === "report" || activeMainPage === "previousAppeal";

  const fetchData = () => {
    if (!id) return;

    setLoading(true);
    fetchDefendantAppealById(id)
      .then((data) => {
        setAppeal(data);
      })
      .catch((error) => {
        showTooltip(
          errorHandlerOnForm(error) || t("error_fetching_case_details"),
          "error",
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isRegistrar = hasRole(loginData?.roles, [Roles.registrar]);
  const isCoordinator = hasRole(loginData?.roles, [Roles.coordinator]);

  const updateStatus = (
    status: keyof typeof DefendantAppealStatus,
    coordinatorStatus = "",
  ) => {
    editDefendantAppeal2({
      id: id,
      status: status,
      coordinator_status: coordinatorStatus
        ? coordinatorStatus
        : appeal?.coordinator_status,
    })
      .then(() => {
        showTooltip(t("successful"), "success");
        fetchData();
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      });
  };

  // Header Component (mirrors the prosecutor Appeal/Cassation case header)
  const renderHeader = () => (
    <div className="relative overflow-hidden bg-white rounded-xl shadow-sm border border-gray-200 px-5 py-4 mb-5">
      {/* Decorative gradient accent */}
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-red-700 via-red-600 to-red-500" />
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-11 h-11 bg-gradient-to-br from-red-500 to-red-700 rounded-xl flex items-center justify-center shrink-0 shadow-md shadow-red-200">
            <span className="text-xl text-white">⚖</span>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-lg font-bold text-gray-900 leading-tight">
                {isCass
                  ? t("defendant_cassation", {
                      defaultValue: "Defendant Cassation",
                    })
                  : t("defendant_appeal")}
              </h1>
              {appeal?.case?.prosecutor_registration_number && (
                <span className="text-sm font-semibold text-gray-400">
                  #{appeal.case.prosecutor_registration_number}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 shrink-0">
          {isCass && hasPreviousAppeal && (
            <ActionButton
              onClick={() =>
                setActiveMainPage(
                  activeMainPage === "previousAppeal"
                    ? "appeal"
                    : "previousAppeal",
                )
              }
              variant={
                activeMainPage === "previousAppeal" ? "primary" : "outline"
              }
            >
              {activeMainPage === "previousAppeal"
                ? `📝 ${t("cassation")}`
                : `📂 ${t("previous_appeal", { defaultValue: "Previous Appeal" })}`}
            </ActionButton>
          )}
          {appeal?.status === "FORWARDED_TO_COURT" && (
            <ActionButton
              onClick={() =>
                setActiveMainPage(
                  activeMainPage === "report" ? "appeal" : "report",
                )
              }
              variant={activeMainPage === "report" ? "primary" : "outline"}
            >
              {activeMainPage === "report"
                ? `📝 ${isCass ? t("cassation") : t("appeal")}`
                : `📊 ${t("report")}`}
            </ActionButton>
          )}
          {appeal?.bail_right && (
            <ActionButton
              onClick={() =>
                navigate("/bail-right/" + appeal?.bail_right?.uu_id + "/")
              }
            >
              ⚖️ {t("bail_right")}
            </ActionButton>
          )}
        </div>
      </div>

    </div>
  );

  // Appeal Content
  const renderAppealContent = () => (
    <div className="space-y-4">
      {/* Prosecutor Response — independent card at the top */}
      {appeal?.response_summary && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100 bg-gray-50/60">
            <div className="w-1.5 h-6 bg-red-600 rounded-full" />
            <h3 className="text-base font-semibold text-gray-900">
              {t("prosecutor_response", {
                defaultValue: "Prosecutor Response",
              })}
            </h3>
          </div>
          <div className="p-5 space-y-4">
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
              {appeal.response_summary}
            </p>
            {(() => {
              // Only the response file (document_type defendant_appeal_response).
              const responseDocs = (appeal?.documents || []).filter(
                (d) => d?.document_type === "defendant_appeal_response",
              );
              if (responseDocs.length === 0) return null;
              return (
                <div className="pt-3 border-t border-gray-100">
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-400 mb-2">
                    {t("response_file")}
                  </p>
                  <DocumentViewerModal
                    documents={responseDocs as any}
                    baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.DEFENDANT_APPEAL}`}
                    layout="list"
                    hideEmptyFile={true}
                  />
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Appeal Request Information */}
      <InfoCard
        title={isCass ? t("cassation_request") : t("appeal_request")}
        defaultOpen={true}
      >
        {/* Highlighted assignment / registrar details */}
        {(appeal?.assigned_prosecutor ||
          appeal?.registrar_number ||
          appeal?.registrar_date) && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            {[
              {
                icon: "👤",
                label: t("assigned_prosecutor"),
                value: appeal?.assigned_prosecutor,
              },
              {
                icon: "🔖",
                label: t("registrar_number"),
                value: appeal?.registrar_number,
              },
              {
                icon: "📅",
                label: t("registrar_date"),
                value: appeal?.registrar_date
                  ? DateView(appeal.registrar_date)
                  : "",
              },
            ]
              .filter((f) => f.value)
              .map((f, i) => (
                <div
                  key={i}
                  className="rounded-xl border border-red-100 bg-red-50/50 p-3"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-base leading-none">{f.icon}</span>
                    <p className="text-[11px] uppercase tracking-wide text-red-700/80 font-semibold">
                      {f.label}
                    </p>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 break-words">
                    {f.value}
                  </p>
                </div>
              ))}
          </div>
        )}

        <div className="space-y-1">
          {[
            {
              label: t("defendant_name"),
              value: appeal?.defendent_name_if_bail_right ?? "-",
            },
            {
              label: t("higher_court_type"),
              value: appeal?.higher_court_type ?? "-",
            },
            {
              label: t("higher_court_level"),
              value: appeal?.higher_court_level ?? "-",
            },
            {
              label: t("higher_court_hearing_date"),
              value: appeal?.higher_court_hearing_date
                ? DateView(appeal?.higher_court_hearing_date)
                : "-",
            },
            { label: t("court_zone"), value: appeal?.court_zone ?? "-" },
            { label: t("court_woreda"), value: appeal?.court_woreda ?? "-" },
            {
              label: isCass
                ? t("court_cassation_number", {
                    defaultValue: "Court Cassation Number",
                  })
                : t("court_appeal_number"),
              value: appeal?.court_appeal_number ?? "-",
            },
            {
              label: t("opening_date"),
              value: appeal?.opening_date
                ? DateView(appeal?.opening_date)
                : "-",
            },
            {
              label: t("first_hearing_date"),
              value: appeal?.first_hearidng_date
                ? DateView(appeal?.first_hearidng_date)
                : "-",
            },
          ].map(
            (item, index) =>
              item.value !== "-" && (
                <InfoRow key={index} label={item.label} value={item.value} />
              ),
          )}
        </div>
      </InfoCard>

      {/* Defendants */}
      {appeal?.defendants && appeal.defendants.length > 0 && (
        <InfoCard title={t("defendants")} badgeCount={appeal.defendants.length}>
          <div className="space-y-3">
            {appeal.defendants.map((defendant: any, index: number) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex-1">
                  <div className="font-medium text-gray-900">
                    {defendant.defendant_info?.first_name}{" "}
                    {defendant.defendant_info?.middle_name}{" "}
                    {defendant.defendant_info?.last_name}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {[
                      defendant.defendant_info?.gender,
                      defendant.defendant_info?.phone_number,
                      defendant.defendant_info?.statement,
                    ]
                      .filter(Boolean)
                      .join(" • ")}
                  </div>
                </div>
                <button
                  onClick={() => setSelectedDefendant(defendant.defendant_info)}
                  className="p-2 text-gray-600 hover:text-red-600 hover:bg-white rounded-lg transition-colors"
                  title={t("view_defendant_details")}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </InfoCard>
      )}

      {/* Summary & Attachments */}
      <InfoCard title={t("summary_attachments")}>
        <div className="space-y-4">
          {appeal?.response_summary && (
            <div>
              <h4 className="font-medium text-gray-900 mb-2">
                {t("response_summary")}
              </h4>
              <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
                {appeal.response_summary}
              </p>
            </div>
          )}

          <div className="space-y-3">
            <DocumentViewerModal
              documents={(appeal?.documents || []) as any}
              baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.DEFENDANT_APPEAL}`}
              layout="list"
              label={t("decision_file")}
              hideEmptyFile={true}
            />
          </div>
        </div>
      </InfoCard>

      {appeal?.id && (
        <DirectorNotesButton
          model={FEEDBACK_MODEL.defendantAppeal}
          objectId={appeal.id}
          role={
            isProsecutor
              ? "prosecutor"
              : isRegistrar
                ? "registrar"
                : isCoordinator
                  ? "coordinator"
                  : undefined
          }
        />
      )}
    </div>
  );

  // Action Dropdown for Defendant Appeal
  const renderDefendantAppealActions = () => (
    <div className="space-y-3">
      {isRegistrar && appeal?.status === "PENDING" && (
        <ActionDropdownItem
          tone="success"
          onClick={() => {
            setActionDropdownOpen(false);
            updateStatus("FORWARDED_TO_COORDINATOR", "PENDING");
          }}
        >
          📤 {t("forward_to_coordinator")}
        </ActionDropdownItem>
      )}
      {isRegistrar && appeal?.status === "PENDING" && (
        <ActionDropdownItem
          tone="neutral"
          onClick={() => {
            setActionDropdownOpen(false);
            setOpenEditAppeal(true);
          }}
        >
          ✏️{" "}
          {isCass
            ? t("edit_cassation_registration", {
                defaultValue: "Edit Cassation Registration",
              })
            : t("edit_appeal_registration")}
        </ActionDropdownItem>
      )}
      {(appeal?.status === "FORWARDED_TO_COORDINATOR" ||
        appeal?.status === "ASSIGNED_TO_PROSECUTOR") &&
        isCoordinator &&
        !appeal?.response_summary &&
        appeal?.id && (
          <DefendantAssignProsecutorPanel
            appealId={appeal.id}
            assignedProsecutor={appeal?.assigned_prosecutor}
            onAssigned={fetchData}
          />
        )}
      {isProsecutor &&
        (appeal?.status === "ASSIGNED_TO_PROSECUTOR" ||
          appeal?.status === "RESPONSE_PREPARED_BY_PROSECUTOR") &&
        appeal?.response_summary && (
          <ActionDropdownItem
            tone="success"
            onClick={() => {
              setActionDropdownOpen(false);
              updateStatus("FORWARDED_TO_COORDINATOR", "PENDING");
            }}
          >
            📤 {t("forward_to_coordinator")}
          </ActionDropdownItem>
        )}
      {(appeal?.status === "ASSIGNED_TO_PROSECUTOR" ||
        appeal?.status === "RESPONSE_PREPARED_BY_PROSECUTOR") &&
        isProsecutor && (
          <div className="rounded-xl border border-red-200 bg-red-50/60 p-4">
            <div className="flex items-start gap-3">
              <span className="text-xl leading-none">📝</span>
              <div className="min-w-0">
                <h4 className="text-sm font-semibold text-gray-900">
                  {appeal?.response_summary
                    ? t("edit_response")
                    : t("add_response")}
                </h4>
                <p className="text-xs text-gray-500 mt-0.5">
                  {t("add_response_desc", {
                    defaultValue:
                      "Prepare the prosecutor's response to this appeal and attach the supporting file.",
                  })}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setActionDropdownOpen(false);
                setOpenResponseForm(true);
              }}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors shadow-sm"
            >
              {appeal?.response_summary
                ? `✏️ ${t("edit_response")}`
                : `➕ ${t("add_response")}`}
            </button>
          </div>
        )}
      {appeal?.status === "FORWARDED_TO_COORDINATOR" &&
        appeal?.response_summary &&
        isCoordinator && (
          <ActionDropdownItem
            tone="warning"
            onClick={() => {
              setActionDropdownOpen(false);
              setOpenRespondBack(true);
            }}
          >
            ↩️ {t("respond_back_to_prosecutor")}
          </ActionDropdownItem>
        )}
      {appeal?.status === "FORWARDED_TO_COORDINATOR" &&
        appeal?.response_summary &&
        isCoordinator && (
          <ActionDropdownItem
            tone="success"
            onClick={() => {
              setActionDropdownOpen(false);
              updateStatus("RESPONDED_BACK_TO_REGISTRAR", "APPROVED");
            }}
          >
            ✅ {t("approve")}
          </ActionDropdownItem>
        )}
      {appeal?.status === "RESPONDED_BACK_TO_REGISTRAR" &&
        appeal?.response_summary &&
        isRegistrar &&
        appeal?.id && (
          <DefendantForwardToCourtPanel
            appealId={appeal.id}
            isCassation={isCass}
            onForwarded={fetchData}
          />
        )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lg:h-full flex flex-col bg-gray-50 py-4">
      <div className="px-4 sm:px-6 lg:px-8 flex flex-col flex-1 lg:min-h-0">
        {/* Back Button */}
        <div className="mb-4 shrink-0">
          {selectedCaseId && (
            <div>
              <BackButton
                onClick={() => {
                  setActiveMainPage("appeal");
                  setSelectedCaseId(undefined);
                }}
              />
            </div>
          )}
        </div>

        {!selectedCaseId ? (
          <div className="flex flex-col flex-1 lg:min-h-0">
            <div className="shrink-0">{renderHeader()}</div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:flex-1 lg:min-h-0">
              {/* Left Column - Appeal Content (full width in full-page views) */}
              <div
                className={`space-y-6 lg:overflow-y-auto lg:min-h-0 lg:pr-1 ${
                  isFullPageView ? "lg:col-span-2" : ""
                }`}
              >
                {activeMainPage === "appeal" && renderAppealContent()}
                {activeMainPage === "previousAppeal" && (
                  <PreviousAppealView
                    prosecutorAppealId={previousProsecutorAppealId}
                    defendantAppealId={previousDefendantAppealId}
                  />
                )}
                {activeMainPage === "report" && appeal && (
                  <DefendantAppealReport
                    daily_report_defendant={
                      appeal?.daily_report_defendant || []
                    }
                    final_verdict_defendant={
                      appeal?.final_verdict_defendant || []
                    }
                    setSelectedAppealDailyReport={setSelectedAppealDailyReport}
                    selectedAppealDailyReport={selectedDailyReport}
                    appealData={appeal}
                    fetchData={fetchData}
                    isProsecutor={isProsecutor}
                    isRegistrar={isRegistrar}
                    isCoordinator={isCoordinator}
                  />
                )}
              </div>

              {/* Right Column - Actions & Feedback (hidden in full-page views) */}
              <div
                className={`space-y-6 lg:overflow-y-auto lg:min-h-0 lg:pr-1 ${
                  isFullPageView ? "hidden" : ""
                }`}
              >
                {activeMainPage === "appeal" && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    {/* Top tabs */}
                    <div className="flex border-b border-gray-200">
                      {[
                        { key: "actions" as const, label: t("case_actions") },
                        { key: "feedback" as const, label: t("feedback") },
                      ].map((tab) => (
                        <button
                          key={tab.key}
                          type="button"
                          onClick={() => setRightTab(tab.key)}
                          className={`relative flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                            rightTab === tab.key
                              ? "text-red-700"
                              : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {tab.label}
                          {rightTab === tab.key && (
                            <span className="absolute bottom-0 inset-x-0 h-0.5 bg-red-600" />
                          )}
                        </button>
                      ))}
                    </div>

                    {/* Actions tab */}
                    {rightTab === "actions" && (
                      <div className="p-6 space-y-2">
                        {renderDefendantAppealActions()}
                      </div>
                    )}

                    {/* Feedback tab */}
                    {rightTab === "feedback" && (
                      <div className="p-6 space-y-4">
                        <div className="flex p-1 bg-gray-100 rounded-lg gap-1">
                          {[
                            {
                              key: "normalFeedback" as const,
                              label: t("normal_feedback"),
                            },
                            {
                              key: "respondBack" as const,
                              label: t("respond_back_feedback"),
                            },
                          ].map((tab) => (
                            <button
                              key={tab.key}
                              type="button"
                              onClick={() => setActiveSidebar(tab.key)}
                              className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                activeSidebar === tab.key
                                  ? "bg-white text-gray-900 shadow-sm"
                                  : "text-gray-500 hover:text-gray-700"
                              }`}
                            >
                              {tab.label}
                            </button>
                          ))}
                        </div>

                        <div>
                          {activeSidebar === "normalFeedback" &&
                            !selectedDailyReport &&
                            appeal?.id && (
                              <AppealFeedbackSection
                                type="defendant"
                                appealId={appeal?.id}
                                normal_feedbacks={
                                  appeal?.normal_feedbacks_defendant || []
                                }
                                fetchData={fetchData}
                              />
                            )}
                          {activeSidebar === "respondBack" &&
                            !selectedDailyReport && (
                              <AppealRespondbackFeedback
                                normal_feedbacks={
                                  appeal?.respondback_feedbacks_defendant || []
                                }
                              />
                            )}
                          {appeal?.id && selectedDailyReport && (
                            <AppealDailyReportFeedbackSection
                              appealId={appeal?.id}
                              dailyReport={selectedDailyReport}
                              type="defendant"
                              fetchData={fetchData}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 lg:min-h-0 lg:overflow-y-auto">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <InstituteOfChargeDetail caseId={selectedCaseId} />
            </div>
          </div>
        )}

        {/* Modals */}
        {selectedDefendant && (
          <SuspectDetailInfo
            suspect={selectedDefendant}
            isOpen={!!selectedDefendant}
            isDefendant={true}
            onClose={() => setSelectedDefendant(undefined)}
          />
        )}

        {openEditAppeal && appeal && (
          <DefendantAppealRequestForm
            appeal={appeal}
            isCassation={isCass}
            onClose={(refetch) => {
              setOpenEditAppeal(false);
              if (refetch) {
                fetchData();
              }
            }}
          />
        )}

        {openAppealForm && appeal && (
          <AppealRequestFromDefendantForm
            appeal={appeal}
            appealType={"ReAppealCase"}
            onClose={() => setOpenAppealForm(false)}
          />
        )}

        {/* Action Modals */}
        {appeal?.id && openRespondBack && (
          <RespondBackComment
            appealId={appeal?.id}
            type="defendant"
            respondBackTo={"ASSIGNED_TO_PROSECUTOR"}
            onClose={(refetch) => {
              setOpenRespondBack(false);
              if (refetch) {
                fetchData();
              }
            }}
          />
        )}


        {appeal?.id && openResponseForm && (
          <DefendantAppealResponseForm
            appeal={appeal}
            onClose={(refetch) => {
              setOpenResponseForm(false);
              if (refetch) {
                fetchData();
              }
            }}
          />
        )}

      </div>
    </div>
  );
};

export default DefendantAppealDetail;
